import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DimensionFilterItem, DimensionsFilter, LogicalOperationType } from '../shared/project/dimensions-filtering/dimensions-filter';
import { DimensionName } from '../shared/project/cards/dimension-name';
import { DimensionValue } from '../shared/project/cards/dimension-value';
import { CardHelper } from './cardHelper';
import { DataService } from '../shared/network/data.service';
import { ProjectService } from '../shared/project/project.service';
import { GUIUpdateOnDimValSelection } from '../shared/project/cards/gui-update-on-dim-val-selection';

@Component({
  selector: 'edit-filter-expression-modal',
  templateUrl: 'edit-filter-expression-modal.component.html',
  styleUrls: ['../import/general.scss', './edit-filter-expression-modal.component.scss']
})
export class EditFilterExpressionModalComponent {
    public name: string;
    public dimensionsFilter: DimensionsFilter;
    public legend: DimensionName[][];
    public dimensionsUniqueValuesCache: { [dimensionIndex: number]: string[] } = {};
    public loadingDimensionsUniqueValuesFor: number[] = [];
    public projectId: string;
    public viewId: string;
    public selectedTabId: string;
    public loading: boolean = false;
    public errorMessage: string | null = null;
    
    // Tab dimensions filter properties
    public tabDimensionsFilter: DimensionValue[][];
    public selectingDimValueForTabDimIndex: number | undefined = undefined;
    
    LogicalOperationType: typeof LogicalOperationType = LogicalOperationType;
    selectingDimensionForItemIndexed: number | undefined = undefined;
    selectingValueForItemIndexed: number | undefined = undefined;
    selectedDimensionName: DimensionName;
    dimensionValuesFilter = '';
    filteredDimensionValues: string[] = [];
    applyDimensionValuesFilter: () => void;

    constructor(@Inject(MAT_DIALOG_DATA) data: any, private dialogRef: MatDialogRef<EditFilterExpressionModalComponent>, 
                private dataService: DataService, private projectService: ProjectService) {
        console.log('injected data:');
        console.log(data);
        this.dimensionsFilter = data.dimensionsFilter;
        this.name = data.name;
        this.legend = data.legend;
        this.projectId = data.projectId;
        this.viewId = data.viewId;
        this.selectedTabId = data.selectedTabId;
        this.tabDimensionsFilter = data.tabDimensionsFilter;
        this.applyDimensionValuesFilter = CardHelper.debounce(() => this.applyDimensionValuesFilterInternal());
        
        // Prevent closing the dialog while loading
        this.dialogRef.disableClose = false;
        this.dialogRef.backdropClick().subscribe(() => {
            if (!this.loading) {
                this.dialogRef.close({ success: false });
            }
        });
        this.dialogRef.keydownEvents().subscribe((event) => {
            if (event.key === 'Escape' && !this.loading) {
                this.dialogRef.close({ success: false });
            }
        });
    }

    public async save() {
        this.loading = true;
        this.errorMessage = null;
        
        try {
            let dimFilter = this.selectedTabId ? DimensionsFilter.FromDimensionValues(this.tabDimensionsFilter) : this.dimensionsFilter;
            let cardsModel = await this.projectService.applyDimensionsFilter(this.viewId, this.selectedTabId, dimFilter);
            this.dialogRef.close({ success: true, cardsModel: cardsModel });
        } catch (error) {
            this.errorMessage = error.message || 'Failed to apply the filter. Please check your expression and try again.';
            this.loading = false;
        }
    }

    public cancel() {
        if (this.loading) {
            return; // Prevent closing while loading
        }
        this.dialogRef.close({ success: false });
    }

    public getFilterExpression(): string {
        if (!this.dimensionsFilter || !this.dimensionsFilter.items || this.dimensionsFilter.items.length === 0) {
            return '';
        }

        let parts: string[] = [];
        
        for (let i = 0; i < this.dimensionsFilter.items.length; i++) {
            let item = this.dimensionsFilter.items[i];
            let itemParts: string[] = [];
            
            // Add operation type (And/Or) - skip for first item
            if (i > 0 && item.operationType !== null && item.operationType !== undefined) {
                if (item.operationType === LogicalOperationType.And) {
                    parts.push('And');
                } else if (item.operationType === LogicalOperationType.Or) {
                    parts.push('Or');
                }
            }
            
            // Add opening brackets
            if (item.openBracketsCount > 0) {
                for (let j = 0; j < item.openBracketsCount; j++) {
                    itemParts.push('(');
                }
            }
            
            // Add Not
            if (item.not) {
                itemParts.push('Not');
            }
            
            // Add value or placeholder
            if (item.value) {
                itemParts.push(item.value);
            } else {
                itemParts.push('[Variable]');
            }
            
            // Add closing brackets
            if (item.closeBracketsCount > 0) {
                for (let j = 0; j < item.closeBracketsCount; j++) {
                    itemParts.push(')');
                }
            }
            
            parts.push(itemParts.join(' '));
        }
        
        return parts.join(' ');
    }

    toggleAnd(item: DimensionFilterItem): void {
        if (item.operationType === LogicalOperationType.And) {
            item.operationType = null;
        } else {
            item.operationType = LogicalOperationType.And;
        }
    }

    toggleOr(item: DimensionFilterItem): void {
        if (item.operationType === LogicalOperationType.Or) {
            item.operationType = null;
        } else {
            item.operationType = LogicalOperationType.Or;
        }
    }

    addOpenBracket(item: DimensionFilterItem): void {
        ++item.openBracketsCount;
    }

    addCloseBracket(item: DimensionFilterItem): void {
        ++item.closeBracketsCount;
    }

    removeOpenBracket(item: DimensionFilterItem): void {
        if (item.openBracketsCount > 0){
            --item.openBracketsCount;
        }
    }

    removeCloseBracket(item: DimensionFilterItem): void {
        if (item.closeBracketsCount > 0){
            --item.closeBracketsCount;
        }
    }

    toggleNot(item: DimensionFilterItem): void {
        item.not = !item.not;
    }

    editItemVal(i: number): void {
        if(this.dimensionsFilter.items[i].dimensionIndex == undefined) {
            this.enableDimensionSelectUI(i);
        } else {
            this.selectedDimensionName = CardHelper.findDimensionInLegend(this.legend, this.dimensionsFilter.items[i].dimensionIndex);
            this.enableDimensionValueSelectUI(i);
        }
    }

    enableDimensionSelectUI(i: number) {
        this.selectingDimensionForItemIndexed = i;
    }

    enableDimensionValueSelectUI(i: number) {
        this.selectingValueForItemIndexed = i;
        this.dimensionValuesFilter = this.dimensionsFilter.items[i].value || '';
        console.log('enableDimensionValueSelectUI - dimensionIndex:', this.dimensionsFilter.items[i].dimensionIndex);
        console.log('dimensionsUniqueValuesCache:', this.dimensionsUniqueValuesCache);
        this.loadUniqueValuesForDimension(this.dimensionsFilter.items[i].dimensionIndex);
        this.applyDimensionValuesFilterInternal();
        console.log('filteredDimensionValues:', this.filteredDimensionValues);
    }

    selectDimension(dimension: DimensionName) {
        this.selectedDimensionName = dimension;
        let dimFilterItem = this.dimensionsFilter.items[this.selectingDimensionForItemIndexed];
        dimFilterItem.dimensionIndex = dimension.index;
        dimFilterItem.bgColor = dimension.bgColor;
        dimFilterItem.fontColor = dimension.fontColor;
        let i = this.selectingDimensionForItemIndexed;
        this.selectingDimensionForItemIndexed = undefined;
        this.enableDimensionValueSelectUI(i);
    }

    selectDimensionValue(val: string) {
        let selectedItem = this.dimensionsFilter.items[this.selectingValueForItemIndexed];
        selectedItem.value = val;
        this.finishDimensionValueSelection();
    }

    cancelDimensionSelection() {
        this.selectingDimensionForItemIndexed = undefined;
    }

    cancelDimensionValueSelection() {
        if(this.selectingValueForItemIndexed) {
            let selectedItem = this.dimensionsFilter.items[this.selectingValueForItemIndexed];
            if(selectedItem.dimensionIndex != undefined
                && !selectedItem.value) {
                    selectedItem.dimensionIndex = undefined;
            }
        }
        this.finishDimensionValueSelection();
    }

    finishDimensionValueSelection() {
        this.selectingValueForItemIndexed = undefined;
        this.dimensionValuesFilter = '';
        this.filteredDimensionValues = [];
    }

    private applyDimensionValuesFilterInternal() {
        console.log('applyDimensionValuesFilterInternal called');
        
        // Determine which dimension index to use
        let dimensionIndex: number;
        if (this.selectingDimValueForTabDimIndex !== undefined) {
            dimensionIndex = this.selectingDimValueForTabDimIndex;
        } else if (this.selectingValueForItemIndexed !== undefined) {
            dimensionIndex = this.dimensionsFilter.items[this.selectingValueForItemIndexed].dimensionIndex;
        } else {
            console.log('Neither selectingDimValueForTabDimIndex nor selectingValueForItemIndexed is set, returning');
            return;
        }
        
        console.log('dimensionIndex:', dimensionIndex);
        this.dimensionsUniqueValuesCache = this.dimensionsUniqueValuesCache || {};
        let cachedVals = this.dimensionsUniqueValuesCache[dimensionIndex];
        console.log('cachedVals:', cachedVals);
        if (!cachedVals) {
            console.log('No cached values found for dimension', dimensionIndex);
            return;
        }
        if(!this.dimensionValuesFilter) {
            console.log('No filter, showing all values');
            this.filteredDimensionValues = cachedVals;
            return;
        }
        console.log('Filtering with:', this.dimensionValuesFilter);
        this.filteredDimensionValues = cachedVals.filter(s => new RegExp(this.dimensionValuesFilter, 'i').test(s));
        console.log('Filtered results:', this.filteredDimensionValues.length, 'items');
    }

    private async loadUniqueValuesForDimension(index: number) {
        console.log('loadUniqueValuesForDimension called for index:', index);
        if (this.loadingDimensionsUniqueValuesFor.indexOf(index) >= 0) {
            console.log('Already loading values for dimension', index);
            return;
        }
        
        // Check if already cached
        if (this.dimensionsUniqueValuesCache[index]) {
            console.log('Values already cached for dimension', index);
            return;
        }
        
        this.loadingDimensionsUniqueValuesFor.push(index);
        try {
            console.log('Fetching unique values for dimension', index, 'projectId', this.projectId);
            let vals = await this.dataService.get<string[]>(`processing/getUniqueValuesForDimension?projectId=${this.projectId}&dimensionIndex=${index}`);
            console.log('Received values:', vals);
            this.dimensionsUniqueValuesCache[index] = vals;
            let ix = this.loadingDimensionsUniqueValuesFor.indexOf(index);
            if (ix > -1) {
                this.loadingDimensionsUniqueValuesFor.splice(ix, 1);
            }
            this.applyDimensionValuesFilterInternal();
        } catch (error) {
            console.error('Error loading dimension values:', error);
            let ix = this.loadingDimensionsUniqueValuesFor.indexOf(index);
            if (ix > -1) {
                this.loadingDimensionsUniqueValuesFor.splice(ix, 1);
            }
        }
    }

    addNewDimensionFilterItem(index: number) {
        if (!this.dimensionsFilter) {
            this.dimensionsFilter = new DimensionsFilter();
            this.dimensionsFilter.items = [];
        }
        let newItem = new DimensionFilterItem();
        newItem.openBracketsCount = 0;
        newItem.closeBracketsCount = 0;
        newItem.not = false;
        newItem.bgColor = '#fde5d7';
        newItem.fontColor = '#0070c1';
        if (this.dimensionsFilter.items) {
            this.dimensionsFilter.items.splice(index, 0, newItem);
        } else {
            this.dimensionsFilter.items = [ newItem ];
        }
    }

    removeDimensionsFilterItem(index: number): void {
        this.dimensionsFilter.items.splice(index, 1);
    }

    // Tab dimensions filter methods
    enableDimensionValueSelectionForTabDimFilter(dim: DimensionValue): void {
        this.selectingDimValueForTabDimIndex = dim.index;
        this.selectedDimensionName = CardHelper.findDimensionInLegend(this.legend, dim.index);
        this.dimensionValuesFilter = dim.value || '';
        this.loadUniqueValuesForDimension(dim.index);
        this.applyDimensionValuesFilterInternal();
    }

    async selectDimensionValueForTab(val: string): Promise<void> {
        let dimIndex = this.selectingDimValueForTabDimIndex;
        let selectedItems: DimensionValue[] = [];
        for (let i = 0; i < this.tabDimensionsFilter.length; ++i) {
            for (let j = 0; j < this.tabDimensionsFilter[i].length; ++j) {
                if (this.tabDimensionsFilter[i][j].index == dimIndex) {
                    selectedItems.push(this.tabDimensionsFilter[i][j]);
                }
            }
        }
        if (selectedItems.every(x => x.value == val)) { //the same value was selected
            this.cancelDimensionValueSelectionForTab();
            return;
        }
        for (let i = 0; i < selectedItems.length; ++i) {
            selectedItems[i].value = val;
            selectedItems[i].isAll = false;
        }
        let res = await this.dataService.get<any>(`Processing/GetGUIUpdateWhenSelectingDimValue?projectId=${this.projectId}&dimensionIndex=${this.selectingDimValueForTabDimIndex}&value=${encodeURIComponent(val)}`);

        for (let i = 0; i < selectedItems.length; ++i) {
            selectedItems[i].fontColor = res.fontColor;
        }
        this.updateDimFilter(res.guiUpdate, this.tabDimensionsFilter);

        this.selectingDimValueForTabDimIndex = undefined;
    }

    private updateDimFilter(guiUpd: GUIUpdateOnDimValSelection, dimFilter: DimensionValue[][]): void {
        for (let i = 0; i < dimFilter.length; ++i) {
            for (let j = 0; j < dimFilter[i].length; ++j) {
                let itemToSetToVal = guiUpd.itemsToSetToVal.find(x => x.dimensionIndex == dimFilter[i][j].index);
                if (itemToSetToVal) {
                    dimFilter[i][j].value = itemToSetToVal.value;
                    dimFilter[i][j].fontColor = itemToSetToVal.fontColor;
                }
                if (guiUpd.dimensionsToSetToAll.indexOf(dimFilter[i][j].index) >= 0) {
                    dimFilter[i][j].value = undefined;
                    dimFilter[i][j].isAll = true;
                }
            }
        }
    }

    cancelDimensionValueSelectionForTab(): void {
        this.selectingDimValueForTabDimIndex = undefined;
        this.dimensionValuesFilter = '';
        this.filteredDimensionValues = [];
    }

    setTabDimValToIsAll(): void {
        let dimIndex = this.selectingDimValueForTabDimIndex;
        for (let i = 0; i < this.tabDimensionsFilter.length; ++i) {
            for (let j = 0; j < this.tabDimensionsFilter[i].length; ++j) {
                let dimVal = this.tabDimensionsFilter[i][j];
                if (dimVal.index == dimIndex) {
                    dimVal.value = null;
                    dimVal.isAll = true;
                }
            }
        }
        this.dimensionValuesFilter = '';
        this.filteredDimensionValues = [];
    }
}
